import { Route as InventoryAvailableRoute } from "@/routes/(pharma)/_layout/inventory/available-inventory/index";
import { useQuery } from "@tanstack/react-query";
import {
    getAvailableMedicalInventory,
    getAvailableMedicalInventoryExpired,
    getAvailableMedicalInventoryExpiring,
    getAvailableNonMedicalInventory,
    getAvailableNonMedicalInventoryExpired,
    getAvailableNonMedicalInventoryExpiring
} from "../-data/api";
import { GetInventoryResponse } from "@/gen/inventory/get_inventory_pb";
import { InventoryLineItem } from "@/gen/inventory/common_pb";

interface InventoryQueryResult {
    items: InventoryLineItem[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export const useInventoryData = () => {
    const searchParams = InventoryAvailableRoute.useSearch();
    
    const page = searchParams.page || 1;
    const pageSize = searchParams.pageSize || 10;
    
    return useQuery<InventoryQueryResult>({
        queryKey: ["inventory", searchParams],
        queryFn: async () => {
            const type = searchParams.type || "MEDICAL";
            const search = searchParams.search || "";
            
            let response: GetInventoryResponse;
            
            if (type === "MEDICAL") {
                if (searchParams.expired) {
                    response = await getAvailableMedicalInventoryExpired(page, pageSize, search);
                } else if (searchParams.expiring_in_30_days) {
                    response = await getAvailableMedicalInventoryExpiring(page, pageSize, search);
                } else {
                    response = await getAvailableMedicalInventory(page, pageSize, search);
                }
            } else {
                // NON_MEDICAL
                if (searchParams.expired) {
                    response = await getAvailableNonMedicalInventoryExpired(page, pageSize, search);
                } else if (searchParams.expiring_in_30_days) {
                    response = await getAvailableNonMedicalInventoryExpiring(page, pageSize, search);
                } else {
                    response = await getAvailableNonMedicalInventory(page, pageSize, search);
                }
            }
            
            // Return structured data with pagination info
            return {
                items: response.items,
                totalCount: response.totalCount,
                page,
                pageSize,
            };
        },
        // Keep previous data while fetching new data for better UX
        placeholderData: (previousData) => previousData,
        // Don't refetch when window regains focus
        refetchOnWindowFocus: false,
        // Stale time - how long data stays fresh (5 minutes)
        staleTime: 5 * 60 * 1000,
        // Cache time - how long inactive data stays in cache (10 minutes)
        gcTime: 10 * 60 * 1000,
    });
};